import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { ActionList, Box, Button, Icon, Text } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { useProfileSetting } from 'hooks/useProfileSetting'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { isProduction } from 'utils/environment'
import track from 'utils/tracker'

export const ManageAccount = () => {
  const { t } = useTranslation()
  const {
    enableLegalAgreements,
    enableIntegrations,
    enableManageMerchants,
    enableUsLaunchRestrictions,
  } = useFeatureFlags()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_ACCOUNT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const { profile } = useProfileContext()
  const { isVerifiedBusinessAccount, isDeveloperAccount } = useAccountsContext()

  const permissions = useMappedRoles()

  const navigate = useNavigate()

  const isAccountViewOnly = !!profile?.viewOnly

  const canDisable =
    !isProduction && isDeveloperAccount ? false : isAccountViewOnly || !isVerifiedBusinessAccount

  const canViewLegalAgreements = permissions?.manageLegalAgreements?.canView ?? false
  const canViewManageChannels = permissions?.channels?.canView
  const canViewManageMerchants = permissions?.manageMerchants?.canView ?? false
  const canViewApiIntegration = permissions?.manageApiIntegration?.canView
  const canViewTeamMembers = permissions?.manageTeamMembers?.canView
  const canViewPaymentSupport = permissions?.paymentSupport?.canView

  const hasLegalAgreementProfileSetting = useProfileSetting('isLegalAgreementSectionEnabled')

  const handleItemClick = (e: { stopPropagation: () => void }, path: string) => {
    e?.stopPropagation()
    navigate(path)
  }

  const settingsItems = [
    ...(enableManageMerchants
      ? [
          {
            key: 'manage-merchants',
            id: 'manage-merchants',
            title: t('settingsManageMerchants'),
            testId: `manage-merchants`,
            disabled: canDisable && !canViewManageMerchants,
            BodyPre: (
              <Text color="faint" variant="body-label" className="pb-1">
                {t('settingsManageMerchantsDescription')}
              </Text>
            ),
            Avatar: (
              <Box paddingL={8} paddingR={4}>
                <Icon name="Storefront" size="large" />
              </Box>
            ),
            onItemClick: e =>
              handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_MERCHANT_DETAILS}`),
            CustomAction: (
              <Button
                onClick={e =>
                  handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_MERCHANT_DETAILS}`)
                }
                iconElement={<Icon name="CaretRight" />}
                variant="ghost"
              />
            ),
          },
        ]
      : []),
    ...(!enableUsLaunchRestrictions
      ? [
          {
            key: 'manage-channels',
            id: 'manage-channels',
            title: t('settingsManageChannels'),
            BodyPre: (
              <Text color="faint" variant="body-label" className="pb-1">
                {t('settingsManageChannelsDescription')}
              </Text>
            ),
            testId: 'manage-channels',
            disabled: !canViewManageChannels,
            Avatar: (
              <Box paddingL={8} paddingR={4}>
                <Icon name="QrCode" size="large" />
              </Box>
            ),
            onItemClick: e =>
              handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_CHANNELS}`),
            CustomAction: (
              <Button
                onClick={e =>
                  handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_CHANNELS}`)
                }
                iconElement={<Icon name="CaretRight" />}
                variant="ghost"
              />
            ),
          },
        ]
      : []),
  ]

  if (canViewPaymentSupport) {
    settingsItems.push({
      key: 'payment-support',
      id: 'payment-support',
      title: t('settingsPaymentSupport'),
      BodyPre: (
        <Text color="faint" variant="body-label" className="pb-1">
          {t('settingsPaymentSupportDescription')}
        </Text>
      ),
      testId: 'payment-support',
      disabled: canDisable,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="Question" size="large" />
        </Box>
      ),
      onItemClick: e =>
        handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_PAYMENT_SUPPORT}`),
      CustomAction: (
        <Button
          onClick={e =>
            handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_PAYMENT_SUPPORT}`)
          }
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  settingsItems.push({
    key: 'manage-crypto-address',
    id: 'manage-crypto-address',
    title: t('settingsManageCryptoAddress'),
    BodyPre: (
      <Text color="faint" variant="body-label" className="pb-1">
        {t('settingsManageCryptoAddressDescription')}
      </Text>
    ),
    testId: 'manage-crypto-address',
    disabled: false,
    Avatar: (
      <Box paddingL={8} paddingR={4}>
        <Icon name="QrCode" size="large" />
      </Box>
    ),
    onItemClick: e =>
      handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_CRYPTO_WHITELISTING}`),
    CustomAction: (
      <Button
        onClick={e =>
          handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_CRYPTO_WHITELISTING}`)
        }
        iconElement={<Icon name="CaretRight" />}
        variant="ghost"
      />
    ),
  })

  if (!isAccountViewOnly && canViewApiIntegration && !enableIntegrations) {
    settingsItems.push({
      key: 'api-integration',
      id: 'api-integration',
      title: t('settingsAPIIntegration'),
      BodyPre: (
        <Text color="faint" variant="body-label" className="pb-1">
          {t('settingsAPIIntegrationDescription')}
        </Text>
      ),
      testId: 'api-integration',
      disabled: false,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="Code" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_API_KEYS}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_API_KEYS}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  if (canViewTeamMembers) {
    settingsItems.push({
      key: 'team-members',
      id: 'team-members',
      title: t('settingsTeamMembers'),
      BodyPre: (
        <Text color="faint" variant="body-label" className="pb-1">
          {t('settingsTeamMembersDescription')}
        </Text>
      ),
      testId: 'team-members',
      disabled: false,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="UsersFour" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_TEAM_MEMBERS}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.MANAGE_TEAM_MEMBERS}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  if (enableLegalAgreements || hasLegalAgreementProfileSetting) {
    settingsItems.unshift({
      key: 'legal-agreements',
      id: 'legal-agreements',
      title: t('legalAgreements.title'),
      BodyPre: (
        <Text color="faint" variant="body-label" className="pb-1">
          {t('legalAgreements.manageDescription')}
        </Text>
      ),
      testId: 'legal-agreements',
      disabled: !canViewLegalAgreements,
      Avatar: (
        <Box paddingL={8} paddingR={4}>
          <Icon name="BuildingOffice" size="large" />
        </Box>
      ),
      onItemClick: e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.LEGAL_AGREEMENTS}`),
      CustomAction: (
        <Button
          onClick={e => handleItemClick(e, `${PathNames.SETTINGS}${PathNames.LEGAL_AGREEMENTS}`)}
          iconElement={<Icon name="CaretRight" />}
          variant="ghost"
        />
      ),
    })
  }

  return (
    <>
      <ActionList id="manage-account" items={settingsItems} />
    </>
  )
}
